import {
  BoxGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  PlaneGeometry,
  Scene,
} from 'three';
import { BLOCK_STEP, CITY, HALF_CITY } from '../config/settings';
import { chance, randFloat } from '../utils/random';
import { TextureLibrary } from '../textures/TextureLibrary';
import { BuildingRegistry } from './BuildingRegistry';
import { BuildingFactory } from './BuildingFactory';
import { addTree } from './Decor';

/**
 * Generates the static city: ground plane, sidewalk slabs, parks and the
 * buildings on each block. Buildings get taller toward the centre, giving a
 * recognisable downtown skyline.
 */
export class CityGrid {
  private readonly group = new Group();
  private readonly buildings: BuildingFactory;

  constructor(
    scene: Scene,
    private readonly textures: TextureLibrary,
    registry: BuildingRegistry,
  ) {
    this.buildings = new BuildingFactory(this.group, textures, registry);
    scene.add(this.group);
    this.createGround(scene);
    this.createBlocks();
  }

  private createGround(scene: Scene): void {
    const size = CITY.GRID * BLOCK_STEP + 400;
    const tex = this.textures.asphalt();
    tex.repeat.set(size / 12, size / 12);
    const ground = new Mesh(new PlaneGeometry(size, size), new MeshLambertMaterial({ map: tex }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
  }

  private createBlocks(): void {
    for (let gx = 0; gx < CITY.GRID; gx++) {
      for (let gz = 0; gz < CITY.GRID; gz++) {
        const cx = -HALF_CITY + CITY.ROAD_WIDTH / 2 + gx * BLOCK_STEP + CITY.BLOCK_SIZE / 2;
        const cz = -HALF_CITY + CITY.ROAD_WIDTH / 2 + gz * BLOCK_STEP + CITY.BLOCK_SIZE / 2;
        this.createSidewalk(cx, cz);
        if (chance(0.12)) this.createPark(cx, cz);
        else this.createBuildings(cx, cz, gx, gz);
      }
    }
  }

  private createSidewalk(cx: number, cz: number): void {
    const tex = this.textures.sidewalk();
    const slab = new Mesh(
      new BoxGeometry(CITY.BLOCK_SIZE, CITY.CURB_HEIGHT, CITY.BLOCK_SIZE),
      new MeshLambertMaterial({ map: tex }),
    );
    slab.position.set(cx, CITY.CURB_HEIGHT / 2, cz);
    slab.receiveShadow = true;
    this.group.add(slab);
  }

  private createPark(cx: number, cz: number): void {
    const grass = new Mesh(
      new BoxGeometry(CITY.BLOCK_SIZE - 8, 0.3, CITY.BLOCK_SIZE - 8),
      new MeshLambertMaterial({ map: this.textures.grass() }),
    );
    grass.position.set(cx, CITY.CURB_HEIGHT + 0.15, cz);
    grass.receiveShadow = true;
    this.group.add(grass);
    for (let i = 0; i < 5; i++) {
      addTree(this.group, cx + randFloat(-18, 18), cz + randFloat(-18, 18), CITY.CURB_HEIGHT + 0.3);
    }
  }

  private createBuildings(cx: number, cz: number, gx: number, gz: number): void {
    const cols = chance(0.55) ? 2 : 1;
    const rows = chance(0.55) ? 2 : 1;
    const pad = 3;
    const cellW = (CITY.BLOCK_SIZE - pad * (cols + 1)) / cols;
    const cellD = (CITY.BLOCK_SIZE - pad * (rows + 1)) / rows;
    const centreBias = this.downtownFactor(gx, gz);

    for (let bx = 0; bx < cols; bx++) {
      for (let bz = 0; bz < rows; bz++) {
        if (chance(0.08)) continue; // empty lot
        const width = cellW * randFloat(0.7, 1);
        const depth = cellD * randFloat(0.7, 1);
        const height = randFloat(22, 60) + centreBias * randFloat(20, 150);
        const px = cx - CITY.BLOCK_SIZE / 2 + pad + cellW * (bx + 0.5) + pad * bx;
        const pz = cz - CITY.BLOCK_SIZE / 2 + pad + cellD * (bz + 0.5) + pad * bz;
        this.buildings.create(px, pz, width, depth, height);
      }
    }
  }

  /** 0 at the city edge, 1 downtown — drives building height. */
  private downtownFactor(gx: number, gz: number): number {
    const mid = (CITY.GRID - 1) / 2;
    const edgeDist = Math.max(Math.abs(gx - mid), Math.abs(gz - mid));
    return 1 - edgeDist / mid;
  }
}
